import { DatePipe } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit, PipeTransform } from "@angular/core";
import { MatDatepicker } from "@angular/material/datepicker";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import {
  addVat,
  generateCheckDigit,
  JobDto,
  JobPipe,
  ListResultDto,
  ResourceMasterDto,
  SearchDto,
  vatAmount,
  WebLayoutDto,
} from "@shared/models";
import { Observable } from "rxjs";
import { LookupValuePipe } from "@modules/common/pipes/lookup-value.pipe";
import { AppInjector } from "@app/app.module";
import {
  ContactPipe,
  CustomerLookupPipe,
  DateExPipe,
  NoZeroPipe,
  PublishSubscribe,
} from "../../common";
import { DataTableDirective, SortOrder, TableColumn } from "../../common/components";
import { CustomerService, JobService, LookupListService, ProductSettingService } from "../../common/services";
type ListType = "all" | "open" | "closed";
type LineItemType = 'STK' | 'LAB' | 'CON' | 'TRV';
interface LineItemTotals {
  typeId: LineItemType;
  amount: number;
  ordered: number;
};

const getTotalsAmount = (typeId: LineItemType, totals: LineItemTotals[]) => {
  return totals?.find(item => item.typeId === typeId)?.amount || 0;
};
const getTotalsOrdered = (typeId: LineItemType, totals: LineItemTotals[]) => {
  return totals?.find(item => item.typeId === typeId)?.ordered || 0;
};

interface RowType extends JobDto {
  resolver?: {
    data: any,
    pipe: PipeTransform,
  };
}
@Component({
  templateUrl: "./list.component.html",
  styleUrls: ["./list.component.scss"],
})
export class ListComponent extends DataTableDirective implements OnInit, OnDestroy  {
  resourceColumns: string[] = [
    "resourceId"
  ];
  resourceLoaded = false; // resources need to be loaded before the Resource Lookups can be displayed
  datePickerOpen = false;
  showJobs: { [key: string]: string } = {
    all: "Job.AllJobs",
    open: "Job.OpenJobs",
    closed: "Job.ClosedJobs",
  };
  showJob: ListType = "open";
  get loggedCol(): TableColumn {
    return {
      field: "logged",
      header: "Job.Logged",
      group: "Job.Job",
      defaultSort: SortOrder.Ascending,
      pipe: [new DatePipe("en-ZA"), "MMM dd"],
      class: "w-8",
    };
  }
  get completedCol(): TableColumn{
    return {
      field: "completed",
      header: "Job.Completed",
      group: "Job.Job",
      defaultSort: SortOrder.Descending,
      pipe: new DateExPipe(),
      class: "w-13",
    };
  }
  get endColumns(): TableColumn[] {
    return [
    // Resource
    {
      group: "Job.Resource",
      field: "resourceId",// data property
      columnId: "resourceId",// used for Sorting, etc...
      queryParam: 'resourceId',// for filters
      header: "Job.Resource",
      defaultSort: SortOrder.Ascending,
      class: "w-8",
      skipFilter: false,
      hidden: true,
    },
    {
      group: "Job.Resource",
      field: "resourceId",
      columnId: "JobResourceCompany",
      header: "Job.ResourceCompany",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      lookupFunc: (item: ResourceMasterDto) => item?.contact?.companyName,
      lookupLong: "ResourceMaster",
    },
    {
      group: "Job.Resource",
      field: "resourceId",
      columnId: "JobResourceName",
      header: "Job.ResourceName",
      defaultSort: SortOrder.None,
      class: "w-10",
      hidden: true,
      skipFilter: true,
      lookupFunc: (item: ResourceMasterDto) => (item?.contact?.firstName || '') + ' ' + (item?.contact?.lastName || ''),
      lookupLong: "ResourceMaster",
    },


    // DEALER
    {
      group: "Job.Dealer",
      field: "dealerContact",// data property
      header: "Job.Dealer",
      defaultSort: SortOrder.None,
      class: "w-10",
      hidden: true,
      pipe: new ContactPipe()
    },
    {
      group: "Job.Dealer",
      field: "dealerContact.companyName",// data property
      header: "Contact.Company",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
    },
    {
      group: "Job.Dealer",
      field: "dealerContact.officeHours",// data property
      header: "Contact.OfficeHours",
      defaultSort: SortOrder.None,
      class: "w-10",
      skipFilter: true,
      hidden: true,
    },
    {
      group: "Job.Dealer",
      field: "dealerContact.emailAddress",// data property
      header: "Contact.Email",
      defaultSort: SortOrder.None,
      class: "w-10",
      skipFilter: true,
      hidden: true,
    },

    // JOB ID
    // UI suggestion: move JOB ID to FIRST and use 'nav' button instead (or whole row is active hit box)
    {
      field: "jobId",
      header: "Job.JobID",
      group: "Job.Job",
      defaultSort: SortOrder.Descending,
      class: "w-10",
      mobile: true,
    },


  ];
}
  get baseColumns(): TableColumn[] {
    return [
      // CUSTOMER
      {
        field: "customerId",
        header: "Customer.CustomerID",
        group: "Job.Customer",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true,
      },
      {
        field: "self",
        header: "Customer.Company",
        group: "Job.Customer",
        defaultSort: SortOrder.None,
        class: "w-10",
        hidden: true,
        skipFilter: true,
        converter: (row: RowType) => {
          // NOTES: keep instance of Pipe on RowType for cache
          const data = {
            customerId: row.customerId,
          };
          row.resolver = row.resolver ? row.resolver : {
            data,
            pipe: new CustomerLookupPipe(this.customerService, this.changeDetector)
          };
          if(data.customerId) {
            return row.resolver.pipe.transform(data.customerId, 'companyName');
          }
          return "";
        },
        // converter: (row: JobDto) => row.customerId,
        // pipe: [new CustomerLookupPipe(this.customerService, this.changeDetector), 'companyName'],
      },

      // CONTACT
      {
        field: "contact",
        header: "Contact.Name",
        group: "Job.Contact",
        defaultSort: SortOrder.Ascending,
        class: "w-20",
        pipe: [new ContactPipe(), 'nameOnly'],
      },
      {
        field: "contact.companyName",
        header: "Contact.Company",
        group: "Job.Contact",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true,
      },
      {
        field: "contact.idNumber",
        header: "Contact.IDNumber",
        group: "Job.Contact",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true,
      },
      {
        field: "contact.mobile",
        header: "Contact.Mobile",
        group: "Job.Contact",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hideHandset: true,
      },
      {
        field: "contact.emailAddress",
        header: "Contact.Email",
        group: "Job.Contact",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true,
      },
      {
        field: "contact.note",
        header: "Contact.Note",
        group: "Job.Contact",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true
      },

      // ADDRESS
      {
        group: "Job.Address",
        columnId: "address.line1",
        field: "address.lines.0",
        header: "Address.Line1",
        defaultSort: SortOrder.Ascending,
        class: "w-20",
        hidden: true
      },
      {
        group: "Job.Address",
        columnId: "address.line2",
        field: "address.lines.1",
        header: "Address.Line2",
        defaultSort: SortOrder.Ascending,
        class: "w-20",
        hidden: true
      },
      {
        group: "Job.Address",
        columnId: "address.line3",
        field: "address.lines.2",
        header: "Address.Line3",
        defaultSort: SortOrder.Ascending,
        class: "w-20",
      },
      {
        group: "Job.Address",
        columnId: "address.line4",
        field: "address.lines.3",
        header: "Address.Line4",
        defaultSort: SortOrder.Ascending,
        class: "w-20",
        hidden: true
      },
      {
        group: "Job.Address",
        columnId: "address.line5",
        field: "address.lines.4",
        header: "Address.Line5",
        defaultSort: SortOrder.Ascending,
        class: "w-20",
        hidden: true
      },
      {
        group: "Job.Address",
        field: "address.postalCode",
        header: "Address.PostalCode",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
      },
      {
        group: "Job.Address",
        field: "address.country",
        header: "Address.Country",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true,
      },

      // MACHINE
      {
        field: "machine.machineId",
        header: "Machine.MachineID",
        group: "Job.Machine",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true,
      },
      {
        field: "machine.modelId",
        header: "Machine.ModelID",
        group: "Job.Machine",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
      },
      {
        field: "machine.serialNumber",
        header: "Machine.Serial",
        group: "Job.Machine",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hidden: true,
      },
      {
        field: "machine.fields.0",
        header: "Machine.Field1",
        group: "Job.Machine",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
      },
      {
        field: "machine.fields.1",
        header: "Machine.Field2",
        group: "Job.Machine",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
      },
      {
        field: "machine.fields.2",
        header: "Machine.Field3",
        group: "Job.Machine",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
      },
      {
        field: "machine.fields.3",
        header: "Machine.Field4",
        group: "Job.Machine",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
      },
      {
        field: "machine.fields.4",
        header: "Machine.Field5",
        group: "Job.Machine",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
      },
      {
        field: "machineCount",
        header: "Job.MachineCount",
        group: "Job.Machine",
        defaultSort: SortOrder.Descending,
        class: "w-5",
        hidden: true,
      },
      {
        field: "machine.model.description",
        header: "Machine.MachineName",
        group: "Job.Machine",
        defaultSort: SortOrder.Descending,
        class: "w-15",
        hidden: true,
      },
      {
        field: "machine.model.categories.0",
        queryParam: "machine.category1id",
        header: "Machine.Category1",
        group: "Job.Machine",
        defaultSort: SortOrder.Descending,
        class: "w-5",
        hidden: true,
        pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeMachineCategory']
      },
      {
        field: "machine.model.categories.1",
        queryParam: "machine.category2id",
        header: "Machine.Category2",
        group: "Job.Machine",
        defaultSort: SortOrder.Descending,
        class: "w-5",
        hidden: true,
        pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeMachineCategory']
      },
      {
        field: "machine.model.categories.3",
        queryParam: "machine.category3id",
        header: "Machine.Category3",
        group: "Job.Machine",
        defaultSort: SortOrder.Descending,
        class: "w-5",
        hidden: true,
        pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeMachineCategory']
      },
      {
        field: "machine.dateOfPurchase",
        header: "Machine.DateOfPurchase",
        group: "Job.Machine",
        defaultSort: SortOrder.Descending,
        class: "w-8",
        pipe: new DateExPipe(),
        hidden: true,
        skipFilter: true,
      },
      {
        field: "machine.dealerBranchId",
        header: "Machine.BranchID",
        group: "Job.Machine",
        defaultSort: SortOrder.None,
        class: "w-10",
        hidden: true,
        lookupShort: "DealerBranch",
      },

      // JOB COLUMNS
      {
        group: "Job.Job",
        field: "statusId",
        header: "Job.StatusID",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
      },
      // {
      //   group: "Job.Job",
      //   field: "statusDescription",
      //   queryParam: "statusId",
      //   header: "Job.StatusDescription",
      //   defaultSort: SortOrder.None,
      //   class: "w-8",
      //   hidden: true,
      //   skipFilter: true,
      //   converter: (row: JobDto) => row.statusId,
      //   pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeJobStatus', false]
      // },
      {
        field: "nextAction",
        header: "Job.NextAction",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        pipe: new DateExPipe(),
        hidden: true,
        skipFilter: true,
      },
      {
        group: "Job.Job",
        field: "repairCodeId",
        header: "Job.CloseCodeID",
        defaultSort: SortOrder.Ascending,
        class: "w-5",
        hidden: true,
      },
      {
        group: "Job.Job",
        field: "repairCodeDescription",
        queryParam: "repairCodeId",
        header: "Job.CloseCodeDescription",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        converter: (row: JobDto) => row.repairCodeId,
        pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeJobClose', false]
      },
      {
        field: "customerRef",
        header: "Job.CustomerReference",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true,
      },
      {
        group: "Job.Job",
        field: "typeId",
        header: "Job.TypeID",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true,
      },
      {
        group: "Job.Job",
        field: "typeDescription",
        queryParam: "typeId",
        header: "Job.TypeDescription",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        converter: (row: JobDto) => row.typeId,
        pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeJobType', false]
      },
      {
        field: "ourRef",
        header: "Job.OurReference",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-20",
        hidden: true,
      },
      {
        field: "field1",
        header: "Job.Field1",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hidden: true,
      },
      {
        field: "field2",
        header: "Job.Field2",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hidden: true,
      },
      {
        field: "field3",
        header: "Job.Field3",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hidden: true,
      },
      {
        field: "field4",
        header: "Job.Field4",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hidden: true,
      },
      {
        field: "field5",
        header: "Job.Field5",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hidden: true,
      },
      {
        field: "booked",
        header: "Job.Booked",
        group: "Job.Job",
        defaultSort: SortOrder.Descending,
        pipe: new DateExPipe(),
        class: "w-13",
        hidden: true,
      },
      {
        field: "self",
        columnId: "days",
        header: "Job.OpenDays",
        group: "Job.Job",
        defaultSort: SortOrder.None,
        pipe: [new JobPipe(), "days"],
        class: "w-5 text-right",
        hidden: true,
        skipFilter: true,
      },
      {
        field: "userCaptureId",
        header: "Job.UserCaptureID",
        group: "Job.Job",
        defaultSort: SortOrder.Ascending,
        class: "w-10",
        hidden: true,
      },
      {
        group: "Job.Job",
        field: "sourceId",
        header: "Job.Source",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true,
      },
      {
        group: "Job.Job",
        field: "repeatCount",
        header: "Job.RepeatCount",
        defaultSort: SortOrder.Descending,
        class: "w-5 text-right",
        hidden: true,
      },
      // {
      //   group: "Job.Job",
      //   field: "sourceDescription",
      //   queryParam: "sourceId",
      //   header: "Job.SourceDescription",
      //   defaultSort: SortOrder.None,
      //   class: "w-8",
      //   hidden: true,
      //   skipFilter: true,
      //   converter: (row: JobDto) => row.sourceId,
      //   pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeJobSource', false]
      // },

      // VISITS
      {
        group: "Job.Visits",
        field: "visits.0.resourceId",
        header: "JobLine.VisitResource",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true
      },
      {
        group: "Job.Visits",
        field: "visits.0.statusId",
        header: "JobLine.VisitStatus",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true
      },
      {
        group: "Job.Visits",
        field: "visitDate",
        header: "Job.Visited",
        defaultSort: SortOrder.Ascending,
        pipe: new DateExPipe(),
        class: "w-8",
        hidden: true
      },

      // Contract
      {
        field: "contract.typeId",
        header: "Contract.TypeID",
        group: "Job.Contract",
        defaultSort: SortOrder.None,
        class: "w-10",
        hidden: true,
      },
      // {
      //   field: "contract.typeDescription",
      //   queryParam: "contract.typeId",
      //   header: "Contract.TypeDescription",
      //   group: "Job.Contract",
      //   defaultSort: SortOrder.None,
      //   class: "w-10",
      //   hidden: true,
      //   skipFilter: true,
      //   converter: (row: JobDto) => row.contract?.typeId,
      //   pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeContractType', false]
      // },

      // Payment Fields
      {
        group: "Job.Payment",
        field: "price-excl",
        header: "JobLine.AmountExcl",
        skipFilter: true,
        defaultSort: SortOrder.None,
        class: "w-8 text-right",
        // pipe: [new NoZeroPipe(), "1.2-2"],
        hidden: true,
        converter: (row: JobDto) => (row.paid + row.unpaid),
      },
      {
        group: "Job.Payment",
        field: "vat-amount",
        header: "JobLine.VatAmount",
        skipFilter: true,
        defaultSort: SortOrder.None,
        class: "w-8 text-right",
        // pipe: [new NoZeroPipe(), "1.2-2"],
        hidden: true,
        converter: (row: JobDto) => vatAmount(row.paid + row.unpaid),
      },
      {
        group: "Job.Payment",
        field: "price-incl",
        header: "JobLine.AmountIncl",
        skipFilter: true,
        defaultSort: SortOrder.None,
        class: "w-8 text-right",
        // pipe: [new NoZeroPipe(), "1.2-2"],
        hidden: true,
        converter: (row: JobDto) => addVat(row.paid + row.unpaid),
      },
      {
        field: "unpaid",
        header: "Job.Unpaid",
        group: "Job.Payment",
        defaultSort: SortOrder.Descending,
        class: "w-10 text-right",
        hidden: true,
        // pipe: [new NoZeroPipe(), "1.2-2"],
      },
      {
        field: "paid",
        header: "Job.Paid",
        group: "Job.Payment",
        defaultSort: SortOrder.Descending,
        class: "w-10 text-right",
        hidden: true,
        // pipe: [new NoZeroPipe(), "1.2-2"],
      },
      {
        field: "invoiceNumber",
        header: "Job.Invoice",
        group: "Job.Payment",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true,
      },
      {
        field: "invoice",
        header: "Job.InvoiceDate",
        group: "Job.Payment",
        defaultSort: SortOrder.Descending,
        pipe: new DateExPipe(),
        class: "w-13",
        hidden: true,
      },
      {
        field: "paymentDate",
        header: "Job.PaidDate",
        group: "Job.Payment",
        defaultSort: SortOrder.Descending,
        pipe: new DateExPipe(),
        class: "w-13",
        hidden: true,
      },

      // Validation
      {
        group: "Job.Validation",
        field: "holdTypeId",
        header: "Job.Hold",
        defaultSort: SortOrder.Ascending,
        class: "w-15",
        hidden: true,
      },
      // {
      //   group: "Job.Validation",
      //   field: "holdTypeDescription",
      //   queryParam: "holdTypeId",
      //   header: "Job.HoldDescription",
      //   defaultSort: SortOrder.None,
      //   class: "w-15",
      //   hidden: true,
      //   // skipFilter: true,
      //   converter: (row: JobDto) => row.holdTypeId,
      //   pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeHoldType', false]
      // },
      {
        field: "errors.0.data",
        header: "Job.ErrorData",
        group: "Job.Validation",
        defaultSort: SortOrder.None,
        class: "w-15",
        hidden: true,
      },
      {
        field: "errors.0.description",
        header: "Job.ErrorDescription",
        group: "Job.Validation",
        defaultSort: SortOrder.None,
        class: "w-20",
        hidden: true,
      },

      // JOB RATING
      {
        field: "validations.0.confirmed",
        header: "JobFeedback.Confirmed",
        group: "Job.Validation",
        defaultSort: SortOrder.Ascending,
        class: "w-5",
        hidden: true,
      },
      {
        field: "validations.0.score",
        header: "JobFeedback.Score",
        group: "Job.Validation",
        defaultSort: SortOrder.Ascending,
        class: "w-5",
        hidden: true,
      },
      {
        field: "validations.0.created",
        header: "JobFeedback.Created",
        group: "Job.Validation",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true,
        pipe: [new DatePipe("en-ZA"), "MMM dd"],
      },
      {
        field: "validations.0.followUp",
        header: "JobFeedback.FollowUp",
        group: "Job.Validation",
        defaultSort: SortOrder.None,
        class: "w-15",
        hidden: true,
        skipFilter: true,
      },

      //RESOURCE RATING
      {
        field: "validations.1.score",
        header: "JobFeedback.ResourceRatingScore",
        group: "Job.Validation",
        defaultSort: SortOrder.Ascending,
        class: "w-5",
        hidden: true,
      },

      // Fault
      {
        group: "Job.Fault",
        header: "Job.FaultCodeID",
        field: "faultCodeId",
        defaultSort: SortOrder.Ascending,
        class: "w-5",
        hidden: true,
      },
      {
        group: "Job.Fault",
        header: "Job.FaultCodeDescription",
        field: "faultCodeDescription",
        queryParam: "faultCodeId",
        defaultSort: SortOrder.None,
        class: "w-15",
        hidden: true,
        // skipFilter: true,
        converter: (row: JobDto) => row.faultCodeId,
        pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeJobFault', false]
      },
      {
        field: "faultDescription",
        header: "Job.FaultDescription",
        group: "Job.Fault",
        defaultSort: SortOrder.Ascending,
        class: "w-8",
        hidden: true,
      },

      // Metrics
      {
        group: "Job.Metrics",
        field: "metrics.decimals.0",
        header: "Metric.Description1",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.1",
        header: "Metric.Description2",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.2",
        header: "Metric.Description3",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.3",
        header: "Metric.Description4",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.4",
        header: "Metric.Description5",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.5",
        header: "Metric.Description6",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.6",
        header: "Metric.Description7",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.7",
        header: "Metric.Description8",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.8",
        header: "Metric.Description9",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
      {
        group: "Job.Metrics",
        field: "metrics.decimals.9",
        header: "Metric.Description10",
        defaultSort: SortOrder.None,
        class: "w-8",
        hidden: true,
        skipFilter: true,
        pipe: new NoZeroPipe()
      },
    ];
  }
  get closedColumns(): TableColumn[] {
    return [
    {
      group: "Job.Job",
      field: "diagnosticCodeId",
      header: "Job.DiagnosticCodeID",
      defaultSort: SortOrder.None,
      class: "w-5",
      hidden: true,
      skipFilter: false,
    },
    {
      group: "Job.Job",
      field: "diagnosticCodeDescription",
      queryParam: "diagnosticCodeId",
      header: "Job.DiagnosticCodeDescription",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row: JobDto) => row.diagnosticCodeId,
      pipe: [new LookupValuePipe(AppInjector.get(LookupListService), this.changeDetector), 'CodeJobDiagnostic', false]
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.ConsumableAmount",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsAmount("CON", row.totals).toString(),
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.ConsumableQty",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsOrdered("CON", row.totals).toString(),
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.LabourAmount",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsAmount("LAB", row.totals).toString(),
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.LabourQty",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsOrdered("LAB", row.totals).toString(),
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.TravelAmount",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsAmount("TRV", row.totals).toString(),
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.TravelQty",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsOrdered("TRV", row.totals).toString(),
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.StockAmount",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsAmount("STK", row.totals).toString(),
      pipe: new NoZeroPipe()
    },
    {
      group: "Job.Job",
      field: "totals",
      header: "JobLine.StockQty",
      defaultSort: SortOrder.None,
      class: "w-8",
      hidden: true,
      skipFilter: true,
      converter: (row) => getTotalsOrdered("STK", row.totals).toString(),
      pipe: new NoZeroPipe()
    },

  ];
}

  dateColumns: string[] = [
    "logged",
    "completed",
    "invoice",
    "paymentDate",
    "booked",
    "machine.model.dateOfPurchase",
    "validations.0.created",
    "nextAction"
  ];

  jobId: string;
  codJobs = false;

  constructor(
    public route: ActivatedRoute,
    private jobService: JobService,
    private customerService: CustomerService,
    private router: Router,
    private pubSub: PublishSubscribe,
    productSetting: ProductSettingService,
    private changeDetector: ChangeDetectorRef
  ) {
    super("JobList");
    this.codJobs = productSetting.booleanValue("JobGlobalFilter");
  }

  setVars(parrs: ParamMap) {
    const jobMode = (parrs.get("mode") as ListType) || "open";
    this.showJob = jobMode;
    // if(jobMode !== this.showJob) {
    //   this.setupColumns();

    // } else {
    //   this.showJob = jobMode;

    // }
    super.setVars(parrs);
  }

  get newColumns() {
    return this.showJob === 'closed'
      ? [...this.baseColumns, this.loggedCol, this.completedCol, ...this.closedColumns, ...this.endColumns]
      : [...this.baseColumns, this.loggedCol, ...this.endColumns];
  }

  // Keep the filter field values
  mergeColumns(newColumns: TableColumn[]) {
    for (const newCol of newColumns) {
        // grab the filter from the existing columns
      const existing = this.columns.find((c) => c.field === newCol.field);
      if (existing) {
        newCol.filter = existing.filter;
      }
    }
    console.log(newColumns.find(c => c.field === 'statusId'))
    return newColumns;
  }

  setupColumns() {
    this.columns = this.newColumns;
  }

  jobLink(row: JobDto | string): any[] {
    const jobId = typeof row === "object" ? row.jobId : row;
    if (this.codJobs) {
      return [
        "..",
        "public",
        jobId,
        generateCheckDigit(jobId),
        { inapp: "true" },
      ];
    } else {
      return ["..", jobId];
    }
  }

  // If Mode changes... we change the columns...search will be triggered after column init
  nonLayoutParamChange(p: ParamMap) {
    if(p.get('mode') !== this.showJob) {
      this.setVars(p);
      this.columns = this.mergeColumns(this.newColumns);// keeping old filters
    } else {
      this.search(false, this.columns);
    }
  }

  setJobMode(mode: ListType) {
    const nav: any = { mode };
    if (this.layoutId) {
      nav.layoutId = this.layoutId;
    }
    this.router.navigate([".", nav], {
      relativeTo: this.route,
      replaceUrl: true,
    });
  }

  navigate(resourceId: string): void {
    const nav: any = { mode: this.showJob };
    if (this.layoutId) {
      nav.layoutId = this.layoutId;
    }
    this.router.navigate([".", nav], {
      relativeTo: this.route,
      replaceUrl: true,
    });
  }

  innerSearch(
    page: number,
    size: number,
    columns: TableColumn[]
  ): Observable<ListResultDto<JobDto>> {
    let sortField = "!jobId";
    const sortCol = columns.find((c) => c.sortOrder > SortOrder.None);
    if (sortCol) {
      sortField =
        (sortCol.sortOrder === SortOrder.Descending ? "!" : "") +
        (sortCol.columnId || sortCol.field);
    }
    const jobSearch: SearchDto = {
      pageNumber: page,
      pageSize: size,
      orderBy: [sortField],
      filters: {},
    };
    switch (this.showJob) {
      case "open":
        jobSearch.filters.closed = "false";
        break;
      case "closed":
        jobSearch.filters.closed = "true";
        break;
    }

    for (const prop of columns) {
      if (prop.filter) {
        jobSearch.filters[prop.queryParam || prop.columnId || prop.field] = prop.filter;
      }
    }

    return this.jobService.queryJobs(jobSearch);
  }

  showResource(resourceId: string): boolean {
    this.pubSub.publish("resource_modal", resourceId);
    return false;
  }

  searchJob() {
    this.router.navigate(this.jobLink(this.jobId), { relativeTo: this.route });
  }

  layoutExtras(data: any) {
    data.mode = this.showJob || "open";
  }

  setLayout(layout: WebLayoutDto) {
    if (layout && layout.data.mode) {
      this.showJob = layout.data.mode;
    }
    super.setLayout(layout);
  }

  showCalendar(calendar: MatDatepicker<any>, event: Event) {
    event.stopPropagation();
    if (!calendar.opened) {
      calendar.open();
    }
    this.datePickerOpen = true;
  }
}
